<template>
  <div class="container admin-table-page">
    <!-- HEADER -->
    <div class="param-title-wrap niif-header">
      <div class="d-flex align-items-center w-100">
        <div class="d-flex align-items-center">
          <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
            <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
          </b-button>
          <h2 class="param-title">{{ labels.title }}</h2>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="param-title-wrap niif-header">
      <div class="d-flex align-items-center w-100">
        <div v-if="!showDetailView" class="filters-container d-flex align-items-center">
          <b-form-select v-model="dialogReportes" class="filter-select">
            <b-form-select-option value="">{{ labels.select }}</b-form-select-option>
            <option v-for="(item, idx) in reportes" :key="'codigo-' + idx" :value="item.codigo">
              {{ item?.codigo }}-{{ item?.nombre }}
            </option>
          </b-form-select>

          <b-form-select v-model="selVigencia" class="filter-select ml-2">
            <b-form-select-option value="">{{ labels.select }}</b-form-select-option>
            <option v-for="vigencia in listVigencia" :key="vigencia.vigencia" :value="vigencia.vigencia">
              {{ vigencia?.vigencia }}
            </option>
          </b-form-select>

          <b-form-select v-if="showSelCompany" v-model="selCompany" class="filter-select ml-2">
            <b-form-select-option value="">{{ labels.select }}</b-form-select-option>
            <option v-for="company in listCompany" :value="company.id" :key="company.id">
              {{ company?.nombre }}
            </option>
          </b-form-select>

          <b-button
            class="consult-btn ml-2"
            @click="f_carga_informacion"
            :disabled="!dialogReportes || !selVigencia || !selCompany"
          >
            <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
            {{ labels.consult }}
          </b-button>
        </div>
      </div>
    </div>

    <div class="admin-table-block mt-4">
      <!-- ESTADO INICIAL -->
      <div v-if="!isLoading && !showDetailView && !showToolbarExtras && listaNotas.length === 0">
        <CorporateDataTable
          :items="[]"
          :fields="[]"
          :labels="tableLabels"
          :loading="false"
          :default-per-page="10"
          :page-options="[10]"
          :narrow="false"
          :table-min-height="400"
          :table-max-height="500"
          :show-search="false"
          :allow-column-toggle="false"
          :enable-sorting="false"
          :show-pagination="false"
          :show-rows-per-page="false"
        >
          <template #empty-state>
            <div class="text-center py-5">
              <font-awesome-icon :icon="['fas', 'search']" size="3x" class="text-muted mb-3" style="opacity: 0.3;" />
              <p class="h4 text-muted">{{ labels.selectFilters }}</p>
            </div>
          </template>
        </CorporateDataTable>
      </div>

      <!-- LOADING -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner></b-spinner>
        <p class="mt-2">{{ labels.loading }}</p>
      </div>

      <!-- TABLA PRINCIPAL -->
      <div v-if="!isLoading && !showDetailView && showToolbarExtras && listaNotas.length > 0">
        <CorporateDataTable
          :items="listaNotas"
          :fields="camposNotas"
          :labels="tableLabels"
          :default-per-page="10"
          :page-options="[10, 20, 50, 100]"
          :narrow="false"
          :table-min-height="490"
          :table-max-height="500"
          :show-search="true"
          :allow-column-toggle="true"
          :enable-sorting="true"
          :filter-defs="tableFilterDefs"
          :toolbar-disabled="false"
        >
          <template #toolbar-actions>
            <div class="notas-toolbar-right">
              <b-button size="sm" class="table-toolbar-add-btn" @click="f_nueva_nota">
                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                {{ labels.createNote }}
              </b-button>
            </div>
          </template>

          <template #cell(numero)="data">
            <div class="text-center"><strong>{{ data.item?.numero }}</strong></div>
          </template>

          <template #cell(descripcion)="data">
            <div class="text-left">{{ data.item?.descripcion }}</div>
          </template>

          <template #cell(definicion)="data">
            <b-button
              style="background-color: #2e507a; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="verDetalleNota(data.item)"
            >
              <i><font-awesome-icon :icon="['fas', 'list']" size="xs"/></i>
            </b-button>
          </template>

          <template #cell(accionEdit)="data">
            <b-button
              style="background-color: #FF924C; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="editNota(data.item.id)"
            >
              <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
            </b-button>
          </template>

          <template #cell(accionDelete)="data">
            <b-button
              style="background-color: #cd2838; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="deleteNota(data.item.id, data.item.numero)"
            >
              <i><font-awesome-icon :icon="['fas', 'trash-alt']" size="xs"/></i>
            </b-button>
          </template>
        </CorporateDataTable>
      </div>

      <!-- TABLA VACÍA -->
      <div v-if="!isLoading && !showDetailView && showToolbarExtras && listaNotas.length === 0" class="text-center py-5">
        <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
        <p class="h4 text-muted">{{ labels.empty }}</p>
      </div>

      <!-- DETALLE -->
      <div v-if="!isLoading && showDetailView" class="detail-content">
        <CorporateDataTable
          :items="a_detalle"
          :fields="camposDetalle"
          :labels="detailTableLabels"
          :default-per-page="10"
          :page-options="[10, 20, 50, 100]"
          :narrow="false"
          :table-min-height="400"
          :table-max-height="400"
          :show-search="true"
          :allow-column-toggle="true"
          :enable-sorting="true"
          :filter-defs="detailFilterDefs"
          :toolbar-disabled="false"
          selectable
          @row-selected="f_actualiza_nota"
        >
          <template #toolbar-actions>
            <div class="detail-toolbar-left d-flex align-items-center">
              <b-button size="sm" variant="secondary" @click="cerrarDetalle">
                <font-awesome-icon :icon="['fas', 'folder-open']" class="mr-1" />
                {{ labels.back }}
              </b-button>
            </div>

            <div class="detail-toolbar-right d-flex align-items-center">
              <b-button size="sm" class="table-toolbar-add-btn" @click="f_nuevo_parrafo">
                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                {{ labels.addParagraph }}
              </b-button>
            </div>
          </template>

          <template #cell(parrafo)="data">
            <div class="text-left">{{ data.item?.parrafo }}</div>
          </template>

          <template #cell(tipo)="data">
            <div class="text-left">{{ data.item?.tipo }}</div>
          </template>

          <template #cell(titulo)="data">
            <div class="text-left">{{ data.item?.titulo }}</div>
          </template>

          <template #cell(orden)="data">
            <div class="text-center">{{ data.item?.orden }}</div>
          </template>

          <template #cell(accionEdit)="data">
            <b-button
              style="background-color: #FF924C; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="editarParrafo(data.item)"
            >
              <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
            </b-button>
          </template>

          <template #cell(accionDelete)="data">
            <b-button
              style="background-color: #cd2838; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="eliminarParrafo(data.item)"
            >
              <i><font-awesome-icon :icon="['fas', 'trash-alt']" size="xs"/></i>
            </b-button>
          </template>
        </CorporateDataTable>
      </div>
    </div>

    <!-- MODAL: CREAR / EDITAR NOTA -->
    <b-modal
      :model-value="showNotaModal"
      :no-close-on-backdrop="true"
      hide-footer
      centered
      size="md"
      :title="showEdita ? labels.editNoteTitle : labels.createNoteTitle"
      header-class="reset-modal-header"
      title-class="reset-modal-title"
      body-class="reset-modal-body"
      class="reset-password-dialog nota-dialog"
      @hide="cerrarNotaModal"
    >
      <div class="uniform-modal-body uniform-modal-body--wide">
        <div class="password-policy-box mb-4">
          <div class="password-policy-title">
            {{ showEdita ? labels.infoEditNoteTitle : labels.infoCreateNoteTitle }}
          </div>
          <div class="password-policy-subtitle">
            {{ showEdita ? labels.infoEditNoteText : labels.infoCreateNoteText }}
          </div>
        </div>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.numero }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-if="showNuevo"
              v-model="formNueva.numero"
              type="number"
              class="form-input"
              :placeholder="labels.numberPlaceholder"
            />
            <b-form-input
              v-else
              :value="formNueva.numero"
              type="text"
              class="form-input"
              disabled
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.nota }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formNueva.descripcion"
              type="text"
              class="form-input"
              :placeholder="labels.notePlaceholder"
            />
          </b-col>
        </b-row>

        <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
          <b-button class="reset-btn-secondary" @click="cerrarNotaModal">
            {{ labels.close }}
          </b-button>
          <b-button v-if="showNuevo" class="reset-btn-primary ml-2" @click="grabeNota">
            {{ labels.create }}
          </b-button>
          <b-button v-if="showEdita" class="reset-btn-primary ml-2" @click="actualizaNota">
            {{ labels.modify }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- MODAL: CREAR / EDITAR PÁRRAFO -->
    <b-modal
      :model-value="showParrafoForm"
      :no-close-on-backdrop="true"
      hide-footer
      centered
      scrollable
      size="xl"
      :title="showParrafoFormEdit ? labels.modifyParagraph : labels.createParagraph"
      header-class="reset-modal-header"
      title-class="reset-modal-title"
      body-class="reset-modal-body"
      class="reset-password-dialog parrafo-dialog"
      @hide="cerrarFormParrafo"
    >
      <div class="uniform-modal-body uniform-modal-body--wide">
        <div class="password-policy-box mb-4">
          <div class="password-policy-title">
            {{ showParrafoFormEdit ? labels.infoEditParagraphTitle : labels.infoCreateParagraphTitle }}
          </div>
          <div class="password-policy-subtitle">
            {{ showParrafoFormEdit ? labels.infoEditParagraphText : labels.infoCreateParagraphText }}
          </div>
        </div>

        <b-row class="mb-2 align-items-center">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>Párrafo</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-input v-model="formParrafo.parrafo" :placeholder="labels.paragraphPlaceholder" />
          </b-col>
        </b-row>

        <b-row class="mb-2 align-items-center">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>Tipo</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-select v-model="formParrafo.tipo" @change="f_actualiza_forma">
              <option value="TFG">Texto Fijo General</option>
              <option value="TFC">Texto Fijo Compañia</option>
              <option value="TVG">Texto Variable General</option>
              <option value="TVC">Texto Variable Compañia</option>
              <option value="TBA">Tabla Contable Saldo Actual</option>
              <option value="TBT">Tabla Contable Saldo Anterior</option>
              <option value="TBC">Tabla Contable Comparativa</option>
              <option value="TBM">Tabla Manual</option>
              <option value="TNA">Tabla Niif Notas Saldo Actual</option>
              <option value="TNT">Tabla Niif Notas Saldo Anterior</option>
              <option value="TNC">Tabla Niif Notas Comparativa</option>
            </b-form-select>
          </b-col>
        </b-row>

        <b-row class="mb-2 align-items-center">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>Título</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-input v-model="formParrafo.titulo" :placeholder="labels.titlePlaceholder" />
          </b-col>
        </b-row>

        <b-row class="mb-2 align-items-center">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>Orden</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-input v-model="formParrafo.orden" type="number" placeholder="Orden" />
          </b-col>
        </b-row>

        <!-- Texto: key fuerza remount limpio por párrafo, value inyecta el HTML directamente -->
        <b-row v-if="showTexto" class="mb-2">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>Texto</b></label>
          </b-col>
          <b-col cols="9">
            <RichTextEditor
              ref="textEditor"
              :key="'editor-' + (formParrafo.id || 'nuevo') + '-' + (formParrafo.parrafo || 'x') + '-' + (formParrafo.tipo || 'x')"
              :value="formParrafo.texto_html"
              :height="400"
              :placeholder="labels.editor_placeholder"
              @input="f_cambia_texto_html"
              @change="f_cambia_texto_html"
            />
            <input type="hidden" v-model="formParrafo.texto" />
          </b-col>
        </b-row>

        <template v-if="showCampo">
          <b-row v-for="i in 10" :key="'campo-' + i" class="mb-2 align-items-center">
            <b-col cols="3" class="text-right">
              <label class="form-label"><b>{{ formParrafo['campo' + (i-1)] }}</b></label>
            </b-col>
            <b-col cols="9">
              <b-form-input
                v-model="formParrafo['valor_campo' + (i-1)]"
                @change="f_cambia_campo(i-1)"
                maxlength="20"
              />
            </b-col>
          </b-row>
        </template>

        <template v-if="showCampoTabla">
          <b-row class="mb-2 align-items-center">
            <b-col cols="3" class="text-right">
              <label class="form-label"><b>{{ formParrafo?.nroFilas }}</b></label>
            </b-col>
            <b-col cols="9">
              <b-form-input v-model="formParrafo.numeroFilas" @change="f_filas" type="number" />
            </b-col>
          </b-row>
          <b-row class="mb-2 align-items-center">
            <b-col cols="3" class="text-right">
              <label class="form-label"><b>{{ formParrafo?.nroColumnas }}</b></label>
            </b-col>
            <b-col cols="9">
              <b-form-input v-model="formParrafo.numeroColumnas" @change="f_columnas" type="number" />
            </b-col>
          </b-row>

          <template v-for="i in 10">
            <b-row v-if="getShowFila(i-1)" :key="'fila-' + i" class="mb-2 align-items-center">
              <b-col cols="3" class="text-right">
                <label class="form-label"><b>{{ formParrafo.filas[i-1]?.d_fila }}</b></label>
              </b-col>
              <b-col cols="9">
                <b-form-input v-model="formParrafo.filas[i-1].descripcion" />
              </b-col>
            </b-row>
          </template>

          <template v-for="i in 10">
            <b-row v-if="getShowColumna(i-1)" :key="'columna-' + i" class="mb-2 align-items-center">
              <b-col cols="3" class="text-right">
                <label class="form-label"><b>{{ formParrafo.columnas[i-1]?.d_columna }}</b></label>
              </b-col>
              <b-col cols="9">
                <b-form-input v-model="formParrafo.columnas[i-1].descripcion" />
              </b-col>
            </b-row>
          </template>
        </template>

        <b-row v-if="showNiif" class="mb-2">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>NIIF</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-input
              v-model="niifSearchTerm"
              @input="filterNiifList"
              :placeholder="labels.searchNiif"
              class="mb-2"
            />
            <div style="height: 200px; overflow-y: auto; border: 1px solid #ccc; padding: 10px;">
              <div class="mb-2" style="border-bottom: 1px solid #eee; padding-bottom: 5px;">
                <label style="cursor: pointer; font-weight: bold;">
                  <input type="checkbox" v-model="selectAllNiif" @change="toggleSelectAllNiif" style="margin-right: 8px;" />
                  {{ labels.selectAllNiif }}
                </label>
              </div>
              <div v-for="niif in filteredNiifList" :key="niif.niif" class="mb-2">
                <label style="cursor: pointer; display: flex; align-items: center;">
                  <input type="checkbox" :value="niif.niif" v-model="formParrafo.niif" style="margin-right: 8px;" />
                  <span style="font-size: 14px;">
                    <strong>{{ niif?.niif }}</strong> - {{ niif?.nameNiif }}
                  </span>
                </label>
              </div>
              <div v-if="filteredNiifList?.length === 0" class="text-center text-muted mt-3">
                {{ labels.noNiifFound }}
              </div>
            </div>
            <div class="mt-2">
              <span style="font-size: 12px; color: #666;">
                <strong>{{ formParrafo.niif?.length }}</strong> {{ labels.niifSelected }}
              </span>
            </div>
          </b-col>
        </b-row>

        <b-row v-if="showCuentas" class="mb-2">
          <b-col cols="3" class="text-right">
            <label class="form-label"><b>{{ labels.cuenta }}</b></label>
          </b-col>
          <b-col cols="9">
            <b-form-input
              v-model="cuentasSearchTerm"
              @input="filterCuentasList"
              :placeholder="labels.searchAccounts"
              class="mb-2"
            />
            <div style="height: 200px; overflow-y: auto; border: 1px solid #ccc; padding: 10px;">
              <div class="mb-2" style="border-bottom: 1px solid #eee; padding-bottom: 5px;">
                <label style="cursor: pointer; font-weight: bold;">
                  <input type="checkbox" v-model="selectAllCuentas" @change="toggleSelectAllCuentas" style="margin-right: 8px;" />
                  {{ labels.selectAllAccounts }}
                </label>
              </div>
              <div v-for="cuenta in filteredCuentasList" :key="cuenta.cuenta" class="mb-2">
                <label style="cursor: pointer; display: flex; align-items: center;">
                  <input type="checkbox" :value="cuenta.cuenta" v-model="formParrafo.cuentas" style="margin-right: 8px;" />
                  <span style="font-size: 14px;">
                    <strong>{{ cuenta?.cuenta }}</strong> - {{ cuenta?.descripcion }}
                  </span>
                </label>
              </div>
              <div v-if="filteredCuentasList?.length === 0" class="text-center text-muted mt-3">
                {{ labels.noAccountsFound }}
              </div>
            </div>
            <div class="mt-2">
              <span style="font-size: 12px; color: #666;">
                <strong>{{ formParrafo.cuentas?.length }}</strong> {{ labels.accountsSelected }}
              </span>
            </div>
          </b-col>
        </b-row>

        <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
          <b-button v-if="showParrafoFormEdit" variant="danger" class="mr-2" @click="eliminarParrafoActual">
            {{ labels.delete }}
          </b-button>
          <b-button class="reset-btn-secondary" @click="cerrarFormParrafo">
            {{ labels.close }}
          </b-button>
          <b-button v-if="showParrafoFormEdit" class="reset-btn-primary ml-2" @click="modificaParrafo">
            {{ labels.modify }}
          </b-button>
          <b-button v-if="!showParrafoFormEdit" class="reset-btn-primary ml-2" @click="creaParrafo">
            {{ labels.create }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Notas from '../../../services/Notas'
import PlanCuentas from '../../../services/PlanCuentas'
import Company from '../../../services/Company'
import Balance from '../../../services/BalanceNiif'
import PlanConsolidacion from '../../../services/PlanConsolidacion'
import RichTextEditor from '@/components/RichTextEditor.vue'
import CorporateDataTable from '../../utils/CorporateDataTable.vue'

export default {
  name: 'Notas',
  components: {
    RichTextEditor,
    CorporateDataTable
  },
  data: () => ({
    showToolbarExtras: false,
    showDetailView: false,
    isLoading: false,
    cuentasSearchTerm: '',
    filteredCuentasList: [],
    niifSearchTerm: '',
    filteredNiifList: [],
    selectAllNiif: false,
    selectAllCuentas: false,
    selVigencia: '',
    selReporte: '',
    reportes: [],
    dialogReportes: '',
    listVigencia: [],
    currentNotaId: null,
    formParrafo: {
      id: null,
      numero: null,
      parrafo: null,
      tipo: null,
      titulo: '',
      orden: '',
      texto: '',
      texto_html: '',
      numeroFilas: '',
      numeroColumnas: '',
      filas: [],
      columnas: [],
      cuentas: [],
      niif: [],
      valor_campo0: '',
      valor_campo1: '',
      valor_campo2: '',
      valor_campo3: '',
      valor_campo4: '',
      valor_campo5: '',
      valor_campo6: '',
      valor_campo7: '',
      valor_campo8: '',
      valor_campo9: '',
      campo0: 'Campo 0',
      campo1: 'Campo 1',
      campo2: 'Campo 2',
      campo3: 'Campo 3',
      campo4: 'Campo 4',
      campo5: 'Campo 5',
      campo6: 'Campo 6',
      campo7: 'Campo 7',
      campo8: 'Campo 8',
      campo9: 'Campo 9',
      nroFilas: '',
      nroColumnas: ''
    },
    formAnterior: {},
    listaNotas: [],
    showSelCompany: false,
    selCompany: '',
    listCompany: [],
    listCuentas: [],
    listNiif: [],
    numero: null,
    nota: "",
    a_detalle: [],
    a_detalle_columnas: [],
    a_detalle_cuentas: [],
    a_detalle_niif: [],
    a_detalle_filas: [],
    a_detalle_tabla: [],
    a_detalle_texto: [],
    a_informacion_tblm: [],
    a_informacion_txfc: [],
    a_informacion_txfg: [],
    a_informacion_txvc: [],
    a_informacion_txvg: [],
    showFila0: false,
    showFila1: false,
    showFila2: false,
    showFila3: false,
    showFila4: false,
    showFila5: false,
    showFila6: false,
    showFila7: false,
    showFila8: false,
    showFila9: false,
    showColumna0: false,
    showColumna1: false,
    showColumna2: false,
    showColumna3: false,
    showColumna4: false,
    showColumna5: false,
    showColumna6: false,
    showColumna7: false,
    showColumna8: false,
    showColumna9: false,
    formNueva: {
      id: null,
      numero: '',
      descripcion: '',
    },
    showNuevo: false,
    showEdita: false,
    showNotaModal: false,
    showParrafoForm: false,
    showParrafoFormEdit: false,
    showTexto: false,
    showCampo: false,
    showCampoTabla: false,
    showCuentas: false,
    showNiif: false,
  }),
  computed: {
    labels() {
      const isEs = this.$store.state._language == "es-ES"
      return {
        title: isEs ? 'Notas' : 'Notes',
        select: isEs ? 'Seleccione...' : 'Select...',
        selectFilters: isEs ? 'Seleccione los filtros para consultar' : 'Select filters to consult',
        consult: isEs ? 'Consultar' : 'Consult',
        createNote: isEs ? 'Crear Nota' : 'Create Note',
        createNoteTitle: isEs ? 'Crear Nota' : 'Create Note',
        editNoteTitle: isEs ? 'Editar Nota' : 'Edit Note',
        infoCreateNoteTitle: isEs ? 'Nueva Nota' : 'New Note',
        infoCreateNoteText: isEs ? 'Ingresa los datos de la nueva nota y guarda los cambios.' : 'Enter the new note data and save changes.',
        infoEditNoteTitle: isEs ? 'Editar Nota' : 'Edit Note',
        infoEditNoteText: isEs ? 'Actualiza la información de la nota y guarda los cambios.' : 'Update the note information and save changes.',
        addParagraph: isEs ? 'Añadir Párrafo' : 'Add Paragraph',
        createParagraph: isEs ? 'Crear Párrafo' : 'Create Paragraph',
        modifyParagraph: isEs ? 'Modificar Párrafo' : 'Modify Paragraph',
        infoCreateParagraphTitle: isEs ? 'Nuevo Párrafo' : 'New Paragraph',
        infoCreateParagraphText: isEs ? 'Ingresa los datos del nuevo párrafo y guarda los cambios.' : 'Enter the new paragraph data and save changes.',
        infoEditParagraphTitle: isEs ? 'Editar Párrafo' : 'Edit Paragraph',
        infoEditParagraphText: isEs ? 'Actualiza la información del párrafo y guarda los cambios.' : 'Update the paragraph information and save changes.',
        numero: isEs ? 'Número' : 'Number',
        nota: isEs ? 'Nota' : 'Note',
        definicion: isEs ? 'Definición' : 'Definition',
        edit: isEs ? 'Editar' : 'Edit',
        delete: isEs ? 'Eliminar' : 'Delete',
        parrafo: isEs ? 'Párrafo' : 'Paragraph',
        parrafo_titulo: isEs ? 'Título' : 'Title',
        parrafo_texto: isEs ? 'Texto' : 'Text',
        tipo: isEs ? 'Tipo' : 'Type',
        titulo: isEs ? 'Título' : 'Title',
        orden: isEs ? 'Orden' : 'Order',
        close: isEs ? 'Cerrar' : 'Close',
        create: isEs ? 'Crear' : 'Create',
        modify: isEs ? 'Modificar' : 'Modify',
        save: isEs ? 'Guardar' : 'Save',
        cancel: isEs ? 'Cancelar' : 'Cancel',
        cuenta: isEs ? 'Cuenta' : 'Account',
        searchAccounts: isEs ? 'Buscar cuentas...' : 'Search accounts...',
        searchNiif: isEs ? 'Buscar NIIF...' : 'Search NIIF...',
        selectAllAccounts: isEs ? 'Seleccionar todas las cuentas filtradas' : 'Select all filtered accounts',
        selectAllNiif: isEs ? 'Seleccionar todos los NIIF filtrados' : 'Select all filtered NIIF',
        noAccountsFound: isEs ? 'No se encontraron cuentas' : 'No accounts found',
        noNiifFound: isEs ? 'No se encontraron NIIF' : 'No NIIF found',
        accountsSelected: isEs ? 'cuenta(s) seleccionada(s)' : 'account(s) selected',
        niifSelected: isEs ? 'NIIF(s) seleccionado(s)' : 'NIIF(s) selected',
        editor_placeholder: isEs ? 'Escribe el contenido aquí...' : 'Write content here...',
        paragraphPlaceholder: isEs ? 'Nombre del párrafo' : 'Paragraph name',
        titlePlaceholder: isEs ? 'Título del párrafo' : 'Paragraph title',
        numberPlaceholder: isEs ? 'Número de la nota' : 'Note number',
        notePlaceholder: isEs ? 'Descripción de la nota' : 'Note description',
        noData: isEs ? 'No hay datos disponibles' : 'No data available',
        noResults: isEs ? 'No se encontraron resultados' : 'No results found',
        loading: isEs ? 'Cargando...' : 'Loading...',
        empty: isEs ? 'No hay registros disponibles' : 'No records available',
        back: isEs ? 'Volver' : 'Back',
        searchPlaceholder: isEs ? 'Buscar en la tabla...' : 'Search in table...',
        rowsPerPage: isEs ? 'Filas por página:' : 'Rows per page:',
        noSort: isEs ? 'Sin orden' : 'No sorting',
        sortAsc: isEs ? 'A-Z / Menor a mayor' : 'A-Z to high',
        sortDesc: isEs ? 'Z-A / Mayor a menor' : 'Z-A to low',
        columns: isEs ? 'Columnas' : 'Columns',
        showAllColumns: isEs ? 'Mostrar todas' : 'Show all',
        filters: isEs ? 'Filtros' : 'Filters',
        clearFilters: isEs ? 'Limpiar filtros' : 'Clear filters',
        filterNumero: isEs ? 'Filtrar número...' : 'Filter number...',
        filterNota: isEs ? 'Filtrar nota...' : 'Filter note...',
        filterParrafo: isEs ? 'Filtrar párrafo...' : 'Filter paragraph...',
        filterTipo: isEs ? 'Filtrar tipo...' : 'Filter type...',
        filterTitulo: isEs ? 'Filtrar título...' : 'Filter title...',
        filterOrden: isEs ? 'Filtrar orden...' : 'Filter order...'
      }
    },
    camposNotas() {
      return [
        { key: 'numero', label: this.labels.numero, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', minWidth: '80px' },
        { key: 'descripcion', label: this.labels.nota, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
        { key: 'definicion', label: this.labels.definicion, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '70px' },
        { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '70px' },
        { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '70px' }
      ]
    },
    camposDetalle() {
      return [
        { key: 'parrafo', label: this.labels.parrafo, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '150px' },
        { key: 'tipo', label: this.labels.tipo, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
        { key: 'titulo', label: this.labels.titulo, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '150px' },
        { key: 'orden', label: this.labels.orden, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', minWidth: '80px' },
        { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '70px' },
        { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '70px' }
      ]
    },
    tableFilterDefs() {
      return [
        { key: 'numero', type: 'text', placeholder: this.labels.filterNumero },
        { key: 'descripcion', type: 'text', placeholder: this.labels.filterNota }
      ]
    },
    detailFilterDefs() {
      return [
        { key: 'parrafo', type: 'text', placeholder: this.labels.filterParrafo },
        { key: 'tipo', type: 'text', placeholder: this.labels.filterTipo },
        { key: 'titulo', type: 'text', placeholder: this.labels.filterTitulo },
        { key: 'orden', type: 'text', placeholder: this.labels.filterOrden }
      ]
    },
    tableLabels() {
      return {
        noRow: this.labels.noData,
        noMatches: this.labels.noResults,
        searchPlaceholder: this.labels.searchPlaceholder,
        rowsPerPage: this.labels.rowsPerPage,
        noSort: this.labels.noSort,
        sortAsc: this.labels.sortAsc,
        sortDesc: this.labels.sortDesc,
        columns: this.labels.columns,
        showAllColumns: this.labels.showAllColumns,
        filters: this.labels.filters,
        clearFilters: this.labels.clearFilters
      }
    },
    detailTableLabels() {
      return {
        noRow: this.labels.noData,
        noMatches: this.labels.noResults,
        searchPlaceholder: this.labels.searchPlaceholder,
        rowsPerPage: this.labels.rowsPerPage,
        noSort: this.labels.noSort,
        sortAsc: this.labels.sortAsc,
        sortDesc: this.labels.sortDesc,
        columns: this.labels.columns,
        showAllColumns: this.labels.showAllColumns,
        filters: this.labels.filters,
        clearFilters: this.labels.clearFilters
      }
    }
  },
  methods: {
    goBack() {
      this.$router.back()
    },

    getShowFila(index) {
      const showMap = {
        0: 'showFila0', 1: 'showFila1', 2: 'showFila2', 3: 'showFila3',
        4: 'showFila4', 5: 'showFila5', 6: 'showFila6', 7: 'showFila7',
        8: 'showFila8', 9: 'showFila9'
      }
      return this[showMap[index]] || false
    },

    getShowColumna(index) {
      const showMap = {
        0: 'showColumna0', 1: 'showColumna1', 2: 'showColumna2', 3: 'showColumna3',
        4: 'showColumna4', 5: 'showColumna5', 6: 'showColumna6', 7: 'showColumna7',
        8: 'showColumna8', 9: 'showColumna9'
      }
      return this[showMap[index]] || false
    },

    f_muestre_fila(fila) {
      const shows = ['showFila0', 'showFila1', 'showFila2', 'showFila3', 'showFila4',
        'showFila5', 'showFila6', 'showFila7', 'showFila8', 'showFila9']
      shows.forEach(s => this[s] = false)
      for (let i = 0; i <= fila && i < 10; i++) {
        this[shows[i]] = true
      }
    },

    f_muestre_columna(columna) {
      const shows = ['showColumna0', 'showColumna1', 'showColumna2', 'showColumna3', 'showColumna4',
        'showColumna5', 'showColumna6', 'showColumna7', 'showColumna8', 'showColumna9']
      shows.forEach(s => this[s] = false)
      for (let i = 0; i <= columna && i < 10; i++) {
        this[shows[i]] = true
      }
    },

    f_filas() {
      let a_filas = []
      let vd_fila = {
        id: 0,
        numero: this.numero,
        parrafo: this.formParrafo.parrafo,
        vigencia: this.selVigencia,
        fila: '',
        d_fila: '',
        descripcion: "",
      }
      const numeroFilas = parseInt(this.formParrafo.numeroFilas) || 0
      for (let i = 1; i <= numeroFilas; i++) {
        vd_fila.d_fila = "Fila " + i
        vd_fila.descripcion = ""
        vd_fila.fila = i
        const df = this.a_detalle_filas.find((fila) => fila.fila == i)
        if (df) {
          vd_fila.descripcion = df.descripcion
        }
        a_filas.push({ fila: vd_fila.fila, d_fila: vd_fila.d_fila, descripcion: vd_fila.descripcion })
      }
      this.formParrafo.filas = a_filas
      this.f_muestre_fila(numeroFilas - 1)
    },

    f_columnas() {
      let a_columnas = []
      let vd_columna = {
        id: 0,
        numero: this.numero,
        parrafo: this.formParrafo.parrafo,
        vigencia: this.selVigencia,
        columna: '',
        d_columna: '',
        descripcion: "",
      }
      const numeroColumnas = parseInt(this.formParrafo.numeroColumnas) || 0
      for (let i = 1; i <= numeroColumnas; i++) {
        vd_columna.d_columna = "Columna " + i
        vd_columna.descripcion = ""
        vd_columna.columna = i
        if (this.a_detalle_columnas) {
          const dc = this.a_detalle_columnas.find((columna) => columna.columna == i)
          if (dc) {
            vd_columna.descripcion = dc.descripcion
          }
        }
        a_columnas.push({ columna: vd_columna.columna, d_columna: vd_columna.d_columna, descripcion: vd_columna.descripcion })
      }
      this.formParrafo.columnas = a_columnas
      this.f_muestre_columna(numeroColumnas - 1)
    },

    f_pide_campo() {
      this.showTexto = false
      this.showCampo = false
      this.showCampoTabla = false
      this.showCuentas = false
      this.showNiif = false

      if (this.formParrafo.tipo == "TFG" || this.formParrafo.tipo == "TFC") {
        this.showTexto = true
        this.showCampo = false
      }
      if (this.formParrafo.tipo == "TVG" || this.formParrafo.tipo == "TVC") {
        this.showTexto = true
        this.showCampo = false
      }
      if (this.formParrafo.tipo == "TBM") {
        this.showCampoTabla = true
      }
      if (this.formParrafo.tipo == "TNA" || this.formParrafo.tipo == "TNT" || this.formParrafo.tipo == "TNC") {
        if (!Array.isArray(this.formParrafo.niif)) {
          this.formParrafo.niif = []
        }
        this.showNiif = true
        this.$nextTick(() => {
          this.filterNiifList()
        })
      }
      if (this.formParrafo.tipo == "TBA" || this.formParrafo.tipo == "TBT" || this.formParrafo.tipo == "TBC") {
        if (!Array.isArray(this.formParrafo.cuentas)) {
          this.formParrafo.cuentas = []
        }
        this.showCuentas = true
        this.$nextTick(() => {
          this.filterCuentasList()
        })
      }
    },

    filterCuentasList() {
      if (!this.cuentasSearchTerm) {
        this.filteredCuentasList = [...this.listCuentas]
      } else {
        const term = this.cuentasSearchTerm.toLowerCase()
        this.filteredCuentasList = this.listCuentas.filter(cuenta =>
          (cuenta.cuenta && cuenta.cuenta.toString().toLowerCase().includes(term)) ||
          (cuenta.descripcion && cuenta.descripcion.toLowerCase().includes(term))
        )
      }
      this.updateSelectAllCuentas()
    },

    toggleSelectAllCuentas() {
      if (this.selectAllCuentas) {
        this.filteredCuentasList.forEach(cuenta => {
          if (!this.formParrafo.cuentas.includes(cuenta.cuenta)) {
            this.formParrafo.cuentas.push(cuenta.cuenta)
          }
        })
      } else {
        this.formParrafo.cuentas = this.formParrafo.cuentas.filter(cuentaId =>
          !this.filteredCuentasList.some(c => c.cuenta === cuentaId)
        )
      }
    },

    updateSelectAllCuentas() {
      if (this.filteredCuentasList.length === 0) {
        this.selectAllCuentas = false
        return
      }
      this.selectAllCuentas = this.filteredCuentasList.every(cuenta =>
        this.formParrafo.cuentas.includes(cuenta.cuenta)
      )
    },

    filterNiifList() {
      if (!this.niifSearchTerm) {
        this.filteredNiifList = [...this.listNiif]
      } else {
        const term = this.niifSearchTerm.toLowerCase()
        this.filteredNiifList = this.listNiif.filter(niif =>
          (niif.niif && niif.niif.toString().toLowerCase().includes(term)) ||
          (niif.nameNiif && niif.nameNiif.toLowerCase().includes(term))
        )
      }
      this.updateSelectAllNiif()
    },

    toggleSelectAllNiif() {
      if (this.selectAllNiif) {
        this.filteredNiifList.forEach(niif => {
          if (!this.formParrafo.niif.includes(niif.niif)) {
            this.formParrafo.niif.push(niif.niif)
          }
        })
      } else {
        this.formParrafo.niif = this.formParrafo.niif.filter(niifId =>
          !this.filteredNiifList.some(n => n.niif === niifId)
        )
      }
    },

    updateSelectAllNiif() {
      if (this.filteredNiifList.length === 0) {
        this.selectAllNiif = false
        return
      }
      this.selectAllNiif = this.filteredNiifList.every(niif =>
        this.formParrafo.niif.includes(niif.niif)
      )
    },

    f_actualiza_forma() {
      this.f_pide_campo()

      if (this.formAnterior.tipo == "TFG") {
        const txfg = this.a_informacion_txfg.filter((parrafo) =>
          parrafo.parrafo != this.formAnterior.parrafo)
        this.a_informacion_txfg = txfg || []
      }
      if (this.formAnterior.tipo == "TFC") {
        const txfc = this.a_informacion_txfc.filter((parrafo) =>
          parrafo.parrafo != this.formAnterior.parrafo)
        this.a_informacion_txfc = txfc || []
      }
      if (this.formAnterior.tipo == "TVG") {
        const txvg = this.a_informacion_txvg.filter((parrafo) =>
          parrafo.parrafo != this.formParrafo.parrafo)
        this.a_informacion_txvg = txvg || []
        const txvgd = this.a_detalle_texto.filter((parrafo) =>
          parrafo.parrafo != this.formParrafo.parrafo)
        this.a_detalle_texto = txvgd || []
        for (let i = 0; i < 10; i++) {
          this.formParrafo['valor_campo' + i] = null
          this.formParrafo['campo' + i] = null
        }
      }
      if (this.formAnterior.tipo == "TVC") {
        const txvc = this.a_informacion_txvc.filter((parrafo) =>
          parrafo.parrafo != this.formParrafo.parrafo)
        this.a_informacion_txvc = txvc || []
        const txvgd = this.a_detalle_texto.filter((parrafo) =>
          parrafo.parrafo != this.formParrafo.parrafo)
        this.a_detalle_texto = txvgd || []
        for (let i = 0; i < 10; i++) {
          this.formParrafo['valor_campo' + i] = null
          this.formParrafo['campo' + i] = null
        }
      }
      if (this.formAnterior.tipo == "TBM") {
        this.formParrafo.numeroFilas = ""
        this.formParrafo.numeroColumnas = ""
        for (let i = 0; i < 10; i++) {
          this['showFila' + i] = false
          this['showColumna' + i] = false
        }
      }

      if (this.formParrafo.tipo == "TFG") {
        const r_txfg = {
          id: 0,
          numero: this.formParrafo.numero,
          parrafo: this.formParrafo.parrafo,
          vigencia: this.formParrafo.vigencia,
          texto: this.formParrafo.texto,
          u_crea: this.formParrafo.u_crea,
          createdAt: this.formParrafo.createdAt,
          u_modifica: 2,
          uspdatedAt: this.formParrafo.createdAt,
        }
        this.a_informacion_txfg.push(r_txfg)
      }
      if (this.formParrafo.tipo == "TFC") {
        const r_txfc = {
          id: 0,
          numero: this.formParrafo.numero,
          parrafo: this.formParrafo.parrafo,
          vigencia: this.formParrafo.vigencia,
          id_compania: this.selCompany,
          texto: this.formParrafo.texto,
          u_crea: this.formParrafo.u_crea,
          createdAt: this.formParrafo.createdAt,
          u_modifica: 2,
          uspdatedAt: this.formParrafo.createdAt,
        }
        this.a_informacion_txfc.push(r_txfc)
      }
      if (this.formParrafo.tipo == "TVG" || this.formParrafo.tipo == "TVC") {
        const rd_txvc = {
          id: 0,
          numero: this.formParrafo.numero,
          parrafo: this.formParrafo.parrafo,
          vigencia: this.formParrafo.vigencia,
          texto: this.formParrafo.texto,
          campo0: null,
          campo1: null,
          campo2: null,
          campo3: null,
          campo4: null,
          campo5: null,
          campo6: null,
          campo7: null,
          campo8: null,
          campo9: null,
          u_crea: this.formParrafo.u_crea,
          createdAt: this.formParrafo.createdAt,
          u_modifica: 2,
          uspdatedAt: this.formParrafo.createdAt,
        }
        this.a_detalle_texto.push(rd_txvc)
        if (this.formParrafo.tipo == "TVG") {
          const ri_txvg = {
            id: 0,
            numero: this.formParrafo.numero,
            parrafo: this.formParrafo.parrafo,
            vigencia: this.formParrafo.vigencia,
            valor_campo0: null,
            valor_campo1: null,
            valor_campo2: null,
            valor_campo3: null,
            valor_campo4: null,
            valor_campo5: null,
            valor_campo6: null,
            valor_campo7: null,
            valor_campo8: null,
            valor_campo9: null,
            u_crea: this.formParrafo.u_crea,
            createdAt: this.formParrafo.createdAt,
            u_modifica: 2,
            uspdatedAt: this.formParrafo.createdAt,
          }
          this.a_informacion_txvg.push(ri_txvg)
        }
        if (this.formParrafo.tipo == "TVC") {
          const ri_txvc = {
            id: 0,
            numero: this.formParrafo.numero,
            parrafo: this.formParrafo.parrafo,
            vigencia: this.formParrafo.vigencia,
            id_compania: this.selCompany,
            valor_campo0: null,
            valor_campo1: null,
            valor_campo2: null,
            valor_campo3: null,
            valor_campo4: null,
            valor_campo5: null,
            valor_campo6: null,
            valor_campo7: null,
            valor_campo8: null,
            valor_campo9: null,
            u_crea: this.formParrafo.u_crea,
            createdAt: this.formParrafo.createdAt,
            u_modifica: 2,
            uspdatedAt: this.formParrafo.createdAt,
          }
          this.a_informacion_txvc.push(ri_txvc)
        }
      }

      if (this.formParrafo.tipo == "TFG") {
        const txfg = this.a_informacion_txfg.find((parrafo) =>
          parrafo.parrafo == this.formParrafo.parrafo)
        if (txfg) {
          this.formParrafo.texto = txfg.texto
        }
      }
      if (this.formParrafo.tipo == "TFC") {
        const txfc = this.a_informacion_txfc.find((parrafo) =>
          parrafo.parrafo == this.formParrafo.parrafo)
        if (txfc) {
          this.formParrafo.texto = txfc.texto
        }
      }
      if (this.formParrafo.tipo == "TVG") {
        const txvg = this.a_informacion_txvg.find((parrafo) =>
          parrafo.parrafo == this.formParrafo.parrafo)
        if (txvg) {
          let txvgd = this.a_detalle_texto.find((txvg) =>
            txvg.parrafo == this.formParrafo.parrafo
          )
          const dt = this.a_detalle_texto.find((variable) =>
            variable.parrafo == this.formParrafo.parrafo
          )
          this.formParrafo.texto = txvgd?.texto || ''
          for (let i = 0; i < 10; i++) {
            this.formParrafo['valor_campo' + i] = dt ? dt['campo' + i] : ''
            this.formParrafo['campo' + i] = "Campo " + i
          }
        }
      }
      if (this.formParrafo.tipo == "TVC") {
        const txvc = this.a_informacion_txvc.find((parrafo) =>
          parrafo.parrafo == this.formParrafo.parrafo)
        if (txvc) {
          let txvgd = this.a_detalle_texto.find((txvg) =>
            txvg.parrafo == this.formParrafo.parrafo
          )
          const dt = this.a_detalle_texto.find((variable) =>
            variable.parrafo == this.formParrafo.parrafo
          )
          this.formParrafo.texto = txvgd?.texto || ''
          for (let i = 0; i < 10; i++) {
            this.formParrafo['valor_campo' + i] = dt ? dt['campo' + i] : ''
            this.formParrafo['campo' + i] = "Campo " + i
          }
        }
      }
      if (this.formParrafo.tipo == "TBM") {
        this.formParrafo.nroFilas = "Numero de Filas"
        this.formParrafo.nroColumnas = "Numero de Columnas"
      }
      if (this.formParrafo.tipo != "TBM") {
        this.formParrafo.nroFilas = ""
        this.formParrafo.nroColumnas = ""
        for (let i = 0; i < 10; i++) {
          this['showFila' + i] = false
          this['showColumna' + i] = false
        }
      }
    },

    f_cambia_campo(campo) {
      const dt = this.a_detalle_texto.findIndex((r_campo) =>
        r_campo.parrafo == this.formParrafo.parrafo)
      if (dt >= 0) {
        this.a_detalle_texto[dt]['campo' + campo] = this.formParrafo['valor_campo' + campo]
      }
    },

    f_cambia_texto_html(html) {
      // Solo actualizamos el caché cuando el usuario edita de verdad.
      // Ignoramos emisiones de montaje/desmontaje del editor (vacías).
      if (!this.showParrafoForm) return
      if (!this.showTexto) return
      if (!this.formParrafo || !this.formParrafo.tipo) return
      if (!['TFG', 'TFC', 'TVG', 'TVC'].includes(this.formParrafo.tipo)) return

      const plano = this.htmlToPlainText(html || '')
      // Nunca sobrescribir el caché con contenido vacío.
      // El usuario puede borrar todo, pero eso requiere guardar; mientras edita,
      // si el editor emite '', no tocamos el caché original.
      if (!plano && !html) return

      this.formParrafo.texto = plano

      const parrafoNum = Number(this.formParrafo.parrafo)

      if (this.formParrafo.tipo === "TFG") {
        const v_idx = this.a_informacion_txfg.findIndex((texto) =>
          Number(texto.parrafo) === parrafoNum
        )
        if (v_idx >= 0) {
          this.a_informacion_txfg[v_idx].texto_html = html
          this.a_informacion_txfg[v_idx].texto = plano
        }
      } else if (this.formParrafo.tipo === "TFC") {
        const v_idx = this.a_informacion_txfc.findIndex((texto) =>
          Number(texto.parrafo) === parrafoNum
        )
        if (v_idx >= 0) {
          this.a_informacion_txfc[v_idx].texto_html = html
          this.a_informacion_txfc[v_idx].texto = plano
        }
      } else if (this.formParrafo.tipo === "TVG" || this.formParrafo.tipo === "TVC") {
        const v_idx = this.a_detalle_texto.findIndex((texto) =>
          Number(texto.parrafo) === parrafoNum
        )
        if (v_idx >= 0) {
          this.a_detalle_texto[v_idx].texto_html = html
          this.a_detalle_texto[v_idx].texto = plano
        }
      }
    },

    htmlToPlainText(html) {
      if (!html) return ''
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      let text = tempDiv.textContent || tempDiv.innerText || ''
      text = text.replace(/\s+/g, ' ').trim()
      return text
    },

    plainTextToHTML(text) {
      if (!text) return ''
      const paragraphs = text.split('\n').filter(p => p.trim())
      if (paragraphs.length === 0) return ''
      if (paragraphs.length === 1) return `<p>${this.escapeHTML(paragraphs[0])}</p>`
      return paragraphs.map(p => `<p>${this.escapeHTML(p)}</p>`).join('')
    },

    escapeHTML(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },

    limpiarDatosNota() {
      this.currentNotaId = null
      this.nota = ""
      this.numero = null
      this.a_detalle = []
      this.a_detalle_texto = []
      this.a_detalle_cuentas = []
      this.a_detalle_niif = []
      this.a_detalle_filas = []
      this.a_detalle_columnas = []
      this.a_detalle_tabla = []
      this.a_informacion_txfg = []
      this.a_informacion_txfc = []
      this.a_informacion_txvg = []
      this.a_informacion_txvc = []
      this.a_informacion_tblm = []
      this.cuentasSearchTerm = ''
      this.niifSearchTerm = ''
      this.selectAllCuentas = false
      this.selectAllNiif = false
      this.showCuentas = false
      this.showNiif = false
      this.showTexto = false
      this.showCampo = false
      this.showCampoTabla = false
      for (let i = 0; i < 10; i++) {
        this['showFila' + i] = false
        this['showColumna' + i] = false
      }
    },

    getCodigoParaNiif(codigoReporte) {
      if (!codigoReporte || !this.reportes.length) return codigoReporte
      const reporte = this.reportes.find(r => r.codigo == codigoReporte)
      return reporte?.libro_notas || codigoReporte
    },

    f_carga_informacion() {
      if (!this.dialogReportes || !this.selVigencia || !this.selCompany) {
        this.$toast?.error('Seleccione un reporte, vigencia y compañía para consultar.')
        return
      }

      this.isLoading = true
      this.showToolbarExtras = false
      this.cerrarDetalle()
      this.limpiarDatosNota()
      this.listaNotas = []

      const codigoParaNiif = this.getCodigoParaNiif(this.dialogReportes)

      if (this.selVigencia && codigoParaNiif) {
        PlanConsolidacion.getPlanConsPorCompVige(this.selVigencia, codigoParaNiif, this.selCompany)
          .then((res) => {
            this.listNiif = res.data.planConsNiif || []
            this.filteredNiifList = [...this.listNiif]
            this.niifSearchTerm = ''
            this.selectAllNiif = false
          })
          .catch((error) => {
            console.error('Error al cargar NIIF:', error)
            this.listNiif = []
            this.filteredNiifList = []
          })
      }

      if (this.selVigencia) {
        PlanCuentas.planCuentPorCompaVig(this.selCompany, this.selVigencia)
          .then((r_cuentas) => {
            this.listCuentas = r_cuentas.data.planCuentas || []
            this.filteredCuentasList = [...this.listCuentas]
            this.cuentasSearchTerm = ''
            this.selectAllCuentas = false
          })
          .catch((error) => {
            console.error('Error al cargar cuentas:', error)
            this.listCuentas = []
            this.filteredCuentasList = []
          })
      }

      Notas.getAllNotas(this.dialogReportes)
        .then((res) => {
          this.listaNotas = res.data.listNotas || []
          this.showToolbarExtras = true
        })
        .catch((err) => {
          console.error(err)
          this.listaNotas = []
          this.showToolbarExtras = true
          this.$toast?.error('Error al consultar las notas')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    actualizaTbl() {
      Notas.getAllNotas(this.dialogReportes)
        .then((res) => {
          this.listaNotas = res.data.listNotas || []
          this.showToolbarExtras = true
        })
        .catch((err) => {
          console.error(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    refrescaTbl() {
      Notas.getAllNotas(this.dialogReportes)
        .then((res) => {
          this.listaNotas = res.data.listNotas || []
        })
        .catch((err) => {
          console.error(err)
        })
    },

    f_nueva_nota() {
      this.formNueva = { id: null, numero: '', descripcion: '' }
      this.showNuevo = true
      this.showEdita = false
      this.showNotaModal = true
    },

    f_nuevo_parrafo() {
      this.formParrafo = {
        id: null,
        numero: this.numero,
        parrafo: '',
        tipo: '',
        titulo: '',
        orden: '',
        texto: '',
        texto_html: '',
        valor_campo0: '', valor_campo1: '', valor_campo2: '', valor_campo3: '', valor_campo4: '',
        valor_campo5: '', valor_campo6: '', valor_campo7: '', valor_campo8: '', valor_campo9: '',
        campo0: 'Campo 0', campo1: 'Campo 1', campo2: 'Campo 2', campo3: 'Campo 3',
        campo4: 'Campo 4', campo5: 'Campo 5', campo6: 'Campo 6', campo7: 'Campo 7',
        campo8: 'Campo 8', campo9: 'Campo 9',
        numeroFilas: '', numeroColumnas: '',
        nroFilas: '', nroColumnas: '',
        filas: [], columnas: [],
        cuentas: [], niif: []
      }
      this.showTexto = false
      this.showCampo = false
      this.showCampoTabla = false
      this.showCuentas = false
      this.showNiif = false
      this.showParrafoForm = true
      this.showParrafoFormEdit = false
      this.formAnterior = { ...this.formParrafo }
    },

    editarParrafo(item) {
      if (!item) return
      this.formParrafo = {
        id: item.id,
        numero: item.numero,
        parrafo: item.parrafo,
        tipo: item.tipo,
        titulo: item.titulo || '',
        orden: item.orden || '',
        texto: '',
        texto_html: '',
        valor_campo0: '', valor_campo1: '', valor_campo2: '', valor_campo3: '', valor_campo4: '',
        valor_campo5: '', valor_campo6: '', valor_campo7: '', valor_campo8: '', valor_campo9: '',
        campo0: 'Campo 0', campo1: 'Campo 1', campo2: 'Campo 2', campo3: 'Campo 3',
        campo4: 'Campo 4', campo5: 'Campo 5', campo6: 'Campo 6', campo7: 'Campo 7',
        campo8: 'Campo 8', campo9: 'Campo 9',
        numeroFilas: '', numeroColumnas: '',
        nroFilas: '', nroColumnas: '',
        filas: [], columnas: [],
        cuentas: [], niif: []
      }

      if (item.tipo === 'TBM') {
        const tablaData = this.a_detalle_tabla.find(t => Number(t.parrafo) === Number(item.parrafo))
        if (tablaData) {
          this.formParrafo.numeroFilas = tablaData.filas || tablaData.numeroFilas || ''
          this.formParrafo.numeroColumnas = tablaData.columnas || tablaData.numeroColumnas || ''
          this.formParrafo.nroFilas = "Numero de Filas"
          this.formParrafo.nroColumnas = "Numero de Columnas"

          const filasData = this.a_detalle_filas.filter(f => Number(f.parrafo) === Number(item.parrafo))
          this.formParrafo.filas = filasData.length > 0
            ? filasData.map(f => ({ fila: f.fila, descripcion: f.descripcion || '', d_fila: 'Fila ' + f.fila }))
            : []

          const columnasData = this.a_detalle_columnas.filter(c => Number(c.parrafo) === Number(item.parrafo))
          this.formParrafo.columnas = columnasData.length > 0
            ? columnasData.map(c => ({ columna: c.columna, descripcion: c.descripcion || '', d_columna: 'Columna ' + c.columna }))
            : []

          this.f_filas()
          this.f_columnas()
        }
      }

      if (['TNA', 'TNT', 'TNC'].includes(item.tipo)) {
        const niifExistentes = this.a_detalle_niif
          .filter(n => Number(n.parrafo) === Number(item.parrafo))
          .map(n => n.niif)
        this.formParrafo.niif = niifExistentes
      }

      if (['TBA', 'TBT', 'TBC'].includes(item.tipo)) {
        const cuentasExistentes = this.a_detalle_cuentas
          .filter(c => Number(c.parrafo) === Number(item.parrafo))
          .map(c => c.cuenta)
        this.formParrafo.cuentas = cuentasExistentes
      }

      let textoEncontrado = ''
      let textoHtmlEncontrado = ''
      const parrafoNum = Number(item.parrafo)

      switch (item.tipo) {
        case 'TFG': {
          const txfg = this.a_informacion_txfg.find(tx => Number(tx.parrafo) === parrafoNum)
          if (txfg) {
            textoEncontrado = txfg.texto || ''
            textoHtmlEncontrado = txfg.texto_html || ''
          }
          break
        }
        case 'TFC': {
          const txfc = this.a_informacion_txfc.find(tx => Number(tx.parrafo) === parrafoNum)
          if (txfc) {
            textoEncontrado = txfc.texto || ''
            textoHtmlEncontrado = txfc.texto_html || ''
          }
          break
        }
        case 'TVG': {
          const txvg = this.a_detalle_texto.find(tx => Number(tx.parrafo) === parrafoNum)
          if (txvg) {
            textoEncontrado = txvg.texto || ''
            textoHtmlEncontrado = txvg.texto_html || ''
          }
          const infoTxvg = this.a_informacion_txvg.find(tx => Number(tx.parrafo) === parrafoNum)
          if (infoTxvg) {
            for (let i = 0; i < 10; i++) {
              this.formParrafo['valor_campo' + i] = infoTxvg['valor_campo' + i] || ''
              this.formParrafo['campo' + i] = 'Campo ' + i
            }
          }
          break
        }
        case 'TVC': {
          const txvc = this.a_detalle_texto.find(tx => Number(tx.parrafo) === parrafoNum)
          if (txvc) {
            textoEncontrado = txvc.texto || ''
            textoHtmlEncontrado = txvc.texto_html || ''
          }
          const infoTxvc = this.a_informacion_txvc.find(tx => Number(tx.parrafo) === parrafoNum)
          if (infoTxvc) {
            for (let i = 0; i < 10; i++) {
              this.formParrafo['valor_campo' + i] = infoTxvc['valor_campo' + i] || ''
              this.formParrafo['campo' + i] = 'Campo ' + i
            }
          }
          break
        }
      }

      if (!textoEncontrado && item.texto) {
        textoEncontrado = item.texto
      }
      if (!textoHtmlEncontrado && item.texto_html) {
        textoHtmlEncontrado = item.texto_html
      }
      if (!textoHtmlEncontrado && textoEncontrado) {
        textoHtmlEncontrado = this.plainTextToHTML(textoEncontrado)
      }

      this.formParrafo.texto = textoEncontrado
      this.formParrafo.texto_html = textoHtmlEncontrado

      this.showTexto = false
      this.showCampo = false
      this.showCampoTabla = false
      this.showCuentas = false
      this.showNiif = false

      this.showParrafoForm = true
      this.showParrafoFormEdit = true

      // Después de que el modal se abra, activar showTexto para que el editor se monte
      // con el HTML correcto vía :value
      this.$nextTick(() => {
        this.f_pide_campo()
      })
    },

    cerrarFormParrafo() {
      this.showParrafoForm = false
      this.showParrafoFormEdit = false
      this.showCuentas = false
      this.showNiif = false
      this.showTexto = false
      this.showCampo = false
      this.showCampoTabla = false

      // Si hay una nota activa, refrescar el caché desde el backend
      // para garantizar que los textos sigan íntegros.
      if (this.showDetailView && this.numero && this.dialogReportes && this.selCompany && this.selVigencia) {
        Balance.getNotas(this.dialogReportes, this.selCompany, this.selVigencia, this.numero)
          .then((result) => {
            if (result && result.data) {
              this.a_detalle = result.data.a_detalle || []
              this.a_detalle_texto = result.data.a_detalle_texto || []
              this.a_informacion_txfg = result.data.a_informacion_txfg || []
              this.a_informacion_txfc = result.data.a_informacion_txfc || []
              this.a_informacion_txvg = result.data.a_informacion_txvg || []
              this.a_informacion_txvc = result.data.a_informacion_txvc || []
              this.a_informacion_tblm = result.data.a_informacion_tblm || []
              this.a_detalle_cuentas = result.data.a_detalle_cuentas || []
              this.a_detalle_niif = result.data.a_detalle_niif || []
              this.a_detalle_filas = result.data.a_detalle_filas || []
              this.a_detalle_columnas = result.data.a_detalle_columnas || []
              this.a_detalle_tabla = result.data.a_detalle_tabla || []
            }
          })
          .catch((error) => {
            console.error('Error al refrescar la nota tras cerrar el modal:', error)
          })
      }
    },

    cerrarNotaModal() {
      this.showNotaModal = false
      this.showNuevo = false
      this.showEdita = false
    },

    verDetalleNota(item) {
      this.currentNotaId = item.id
      this.nota = item.descripcion
      this.numero = item.numero
      this.showDetailView = true
      this.isLoading = true

      Balance.getNotas(
        this.dialogReportes,
        this.selCompany,
        this.selVigencia,
        item.numero
      )
        .then((result) => {
          this.a_detalle = result.data.a_detalle || []
          this.a_detalle_texto = result.data.a_detalle_texto || []
          this.a_informacion_txfg = result.data.a_informacion_txfg || []
          this.a_informacion_txfc = result.data.a_informacion_txfc || []
          this.a_informacion_txvg = result.data.a_informacion_txvg || []
          this.a_informacion_txvc = result.data.a_informacion_txvc || []
          this.a_informacion_tblm = result.data.a_informacion_tblm || []
          this.a_detalle_cuentas = result.data.a_detalle_cuentas || []
          this.a_detalle_niif = result.data.a_detalle_niif || []
          this.a_detalle_filas = result.data.a_detalle_filas || []
          this.a_detalle_columnas = result.data.a_detalle_columnas || []
          this.a_detalle_tabla = result.data.a_detalle_tabla || []
        })
        .catch((error) => {
          console.error('Error al cargar nota:', error)
          this.$toast?.error("Error al cargar los detalles de la nota")
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    cerrarDetalle() {
      this.showDetailView = false
      this.currentNotaId = null
      this.nota = ""
      this.numero = null
      this.a_detalle = []
      this.cerrarFormParrafo()
    },

    f_actualiza_nota(item) {
      if (!item || !item.length) return
      const selected = item[0]
      if (selected) {
        this.editarParrafo(selected)
      }
    },

    creaParrafo() {
      if (this.formParrafo.parrafo == '') {
        this.$toast?.error("Campo(s) pendiente(s) por diligenciar")
        return
      }
      if (this.formParrafo.tipo == '') {
        this.$toast?.error("Campo(s) pendiente(s) por diligenciar")
        return
      }

      if (['TFG', 'TFC', 'TVG', 'TVC'].includes(this.formParrafo.tipo)) {
        let texto_plano = ''
        if (this.$refs.textEditor && this.$refs.textEditor.getText) {
          texto_plano = this.$refs.textEditor.getText()
        }
        if (!texto_plano || texto_plano.trim() === '') {
          this.$toast?.error("El texto no puede estar vacío para este tipo de párrafo")
          return
        }
      }

      if (['TBA', 'TBT', 'TBC'].includes(this.formParrafo.tipo)) {
        if (!Array.isArray(this.formParrafo.cuentas) || this.formParrafo.cuentas.length === 0) {
          this.$toast?.error("Debe seleccionar al menos una cuenta para el tipo " + this.formParrafo.tipo)
          return
        }
      }

      if (['TNA', 'TNT', 'TNC'].includes(this.formParrafo.tipo)) {
        if (!this.formParrafo.niif || this.formParrafo.niif.length === 0) {
          this.$toast?.error("Debe seleccionar al menos un NIIF")
          return
        }
      }

      if (this.formParrafo.tipo === 'TBM') {
        if (!this.formParrafo.numeroFilas || !this.formParrafo.numeroColumnas) {
          this.$toast?.error("Debe especificar número de filas y columnas")
          return
        }
      }

      const datosParaEnviar = {
        codReporte: this.dialogReportes,
        numero: this.numero,
        parrafo: this.formParrafo.parrafo,
        tipo: this.formParrafo.tipo,
        titulo: this.formParrafo.titulo || '',
        orden: this.formParrafo.orden || '1',
        vigencia: this.selVigencia,
        idCompania: this.selCompany,
        idUser: this.$store.state.userId || 1
      }

      if (['TFG', 'TFC', 'TVG', 'TVC'].includes(this.formParrafo.tipo)) {
        let texto_html = ''
        let texto_plano = ''
        if (this.$refs.textEditor && this.$refs.textEditor.getHTML) {
          texto_html = this.$refs.textEditor.getHTML()
        }
        if (this.$refs.textEditor && this.$refs.textEditor.getText) {
          texto_plano = this.$refs.textEditor.getText()
        }
        datosParaEnviar.texto = texto_plano
        datosParaEnviar.texto_html = texto_html
      }

      if (this.formParrafo.tipo === 'TVG' || this.formParrafo.tipo === 'TVC') {
        for (let i = 0; i < 10; i++) {
          datosParaEnviar[`valor_campo${i}`] = this.formParrafo[`valor_campo${i}`] || ''
        }
        if (this.formParrafo.tipo === 'TVC') {
          datosParaEnviar.idCompania = this.selCompany
        }
      }

      if (['TBA', 'TBT', 'TBC'].includes(this.formParrafo.tipo)) {
        datosParaEnviar.cuentas = this.formParrafo.cuentas
          .map(c => c ? c.toString().trim() : '')
          .filter(c => c !== '')
      }

      if (['TNA', 'TNT', 'TNC'].includes(this.formParrafo.tipo)) {
        datosParaEnviar.niif = Array.isArray(this.formParrafo.niif) ? this.formParrafo.niif : []
      }

      if (this.formParrafo.tipo === 'TBM') {
        datosParaEnviar.numeroFilas = this.formParrafo.numeroFilas
        datosParaEnviar.numeroColumnas = this.formParrafo.numeroColumnas
        datosParaEnviar.filas = this.formParrafo.filas || []
        datosParaEnviar.columnas = this.formParrafo.columnas || []
      }

      Notas.createParrafo(datosParaEnviar)
        .then(() => {
          this.$toast?.success("Párrafo Creado")
          this.cerrarFormParrafo()
          return Balance.getNotas(this.dialogReportes, this.selCompany, this.selVigencia, this.numero)
        })
        .then((result) => {
          if (result && result.data) {
            this.a_detalle = result.data.a_detalle || []
            this.a_detalle_texto = result.data.a_detalle_texto || []
            this.a_informacion_txfg = result.data.a_informacion_txfg || []
            this.a_informacion_txfc = result.data.a_informacion_txfc || []
            this.a_informacion_txvg = result.data.a_informacion_txvg || []
            this.a_informacion_txvc = result.data.a_informacion_txvc || []
            this.a_informacion_tblm = result.data.a_informacion_tblm || []
            this.a_detalle_cuentas = result.data.a_detalle_cuentas || []
            this.a_detalle_niif = result.data.a_detalle_niif || []
            this.a_detalle_filas = result.data.a_detalle_filas || []
            this.a_detalle_columnas = result.data.a_detalle_columnas || []
            this.a_detalle_tabla = result.data.a_detalle_tabla || []
          }
        })
        .catch((error) => {
          console.error('Error al crear párrafo:', error)
          this.$toast?.error("Error al crear el Párrafo")
        })
    },

    modificaParrafo() {
      let texto_html = ''
      let texto_plano = ''
      if (this.$refs.textEditor && this.$refs.textEditor.getHTML) {
        texto_html = this.$refs.textEditor.getHTML()
      }
      if (this.$refs.textEditor && this.$refs.textEditor.getText) {
        texto_plano = this.$refs.textEditor.getText()
      }

      if (['TFG', 'TFC', 'TVG', 'TVC'].includes(this.formParrafo.tipo)) {
        if (!texto_plano || texto_plano.trim() === '') {
          this.$toast?.error("El texto no puede estar vacío para este tipo de párrafo")
          return
        }
      }

      if (['TBA', 'TBT', 'TBC'].includes(this.formParrafo.tipo)) {
        if (!this.formParrafo.cuentas || this.formParrafo.cuentas.length === 0) {
          this.$toast?.error("Debe seleccionar al menos una cuenta")
          return
        }
      }

      if (['TNA', 'TNT', 'TNC'].includes(this.formParrafo.tipo)) {
        if (!this.formParrafo.niif || this.formParrafo.niif.length === 0) {
          this.$toast?.error("Debe seleccionar al menos un NIIF")
          return
        }
      }

      if (this.formParrafo.tipo === 'TBM') {
        if (!this.formParrafo.numeroFilas || !this.formParrafo.numeroColumnas) {
          this.$toast?.error("Debe especificar número de filas y columnas")
          return
        }
      }

      const datosParaEnviar = {
        id: this.formParrafo.id,
        codReporte: this.dialogReportes,
        numero: this.numero,
        parrafo: this.formParrafo.parrafo,
        tipo: this.formParrafo.tipo,
        titulo: this.formParrafo.titulo || '',
        orden: this.formParrafo.orden || '',
        texto: texto_plano,
        texto_html: texto_html,
        vigencia: this.selVigencia,
        idCompania: this.selCompany,
        idUser: this.$store.state.userId || 1
      }

      if (this.formParrafo.tipo === 'TVG' || this.formParrafo.tipo === 'TVC') {
        for (let i = 0; i < 10; i++) {
          datosParaEnviar[`valor_campo${i}`] = this.formParrafo[`valor_campo${i}`] || ''
        }
      }

      if (this.formParrafo.tipo === 'TBM') {
        datosParaEnviar.numeroFilas = this.formParrafo.numeroFilas
        datosParaEnviar.numeroColumnas = this.formParrafo.numeroColumnas
        datosParaEnviar.filas = this.formParrafo.filas || []
        datosParaEnviar.columnas = this.formParrafo.columnas || []
      }

      if (['TNA', 'TNT', 'TNC'].includes(this.formParrafo.tipo)) {
        datosParaEnviar.niif = this.formParrafo.niif || []
      }

      if (['TBA', 'TBT', 'TBC'].includes(this.formParrafo.tipo)) {
        datosParaEnviar.cuentas = this.formParrafo.cuentas || []
      }

      Notas.updateParrafo(datosParaEnviar)
        .then((response) => {
          this.$toast?.success(response.data.message || "Párrafo Modificado")
          this.cerrarFormParrafo()
          return Balance.getNotas(this.dialogReportes, this.selCompany, this.selVigencia, this.numero)
        })
        .then((result) => {
          if (result && result.data) {
            this.a_detalle = result.data.a_detalle || []
            this.a_detalle_texto = result.data.a_detalle_texto || []
            this.a_informacion_txfg = result.data.a_informacion_txfg || []
            this.a_informacion_txfc = result.data.a_informacion_txfc || []
            this.a_informacion_txvg = result.data.a_informacion_txvg || []
            this.a_informacion_txvc = result.data.a_informacion_txvc || []
            this.a_informacion_tblm = result.data.a_informacion_tblm || []
          }
        })
        .catch((error) => {
          console.error('Error al modificar párrafo:', error)
          this.$toast?.error("Error al modificar el Párrafo")
        })
    },

    eliminarParrafo(item) {
      if (!confirm(`¿Está seguro de eliminar el párrafo ${item.parrafo}?`)) {
        return
      }

      Notas.deleteParrafo(
        this.dialogReportes,
        this.numero,
        item.parrafo,
        this.selVigencia,
        this.selCompany
      )
        .then(() => {
          this.$toast?.success("Párrafo Eliminado")
          this.cerrarFormParrafo()
          return Balance.getNotas(this.dialogReportes, this.selCompany, this.selVigencia, this.numero)
        })
        .then((result) => {
          if (result && result.data) {
            this.a_detalle = result.data.a_detalle || []
            this.a_detalle_texto = result.data.a_detalle_texto || []
            this.a_informacion_txfg = result.data.a_informacion_txfg || []
            this.a_informacion_txfc = result.data.a_informacion_txfc || []
            this.a_informacion_txvg = result.data.a_informacion_txvg || []
            this.a_informacion_txvc = result.data.a_informacion_txvc || []
            this.a_informacion_tblm = result.data.a_informacion_tblm || []
          }
        })
        .catch((error) => {
          console.error('Error al eliminar párrafo:', error)
          this.$toast?.error("Error al eliminar el Párrafo")
        })
    },

    eliminarParrafoActual() {
      this.eliminarParrafo(this.formParrafo)
    },

    editNota(id) {
      const ed = this.listaNotas.find((nota) => nota.id == id)
      if (ed) {
        this.formNueva.id = id
        this.formNueva.numero = ed.numero
        this.formNueva.descripcion = ed.descripcion
        this.showEdita = true
        this.showNuevo = false
        this.showNotaModal = true
      }
    },

    deleteNota(id, numero) {
      if (!confirm(`¿Está seguro de eliminar la nota ${numero}?`)) {
        return
      }

      Notas.deleteNota(id, this.dialogReportes, numero)
        .then(() => {
          this.$toast?.success('Nota eliminada exitosamente')
          if (this.showDetailView) {
            this.cerrarDetalle()
          }
          this.actualizaTbl()
        })
        .catch((error) => {
          console.error('Error al eliminar nota:', error)
          this.$toast?.error(error.response?.data?.message || 'Error al eliminar la nota')
        })
    },

    grabeNota() {
      if (this.formNueva.numero == '' || this.formNueva.descripcion == '') {
        this.$toast?.error("Campo(s) pendiente(s) por diligenciar")
        return
      }

      const formData = {
        codReporte: this.dialogReportes,
        numero: this.formNueva.numero,
        nota: this.formNueva.descripcion,
        idUser: this.$store.state.userId || 1
      }

      Notas.createNota(formData)
        .then(() => {
          this.$toast?.success('Nota creada exitosamente')
          this.refrescaTbl()
          this.showNuevo = false
          this.showNotaModal = false
        })
        .catch((err) => {
          console.error('Error al crear nota:', err)
          this.$toast?.error(err.response?.data?.message || 'Error al crear la nota')
        })
    },

    actualizaNota() {
      if (this.formNueva.descripcion == '') {
        this.$toast?.error("Campo pendiente por diligenciar")
        return
      }

      const formData = {
        id: this.formNueva.id,
        codReporte: this.dialogReportes,
        numero: this.formNueva.numero,
        nota: this.formNueva.descripcion,
        idUser: this.$store.state.userId || 1
      }

      Notas.updateNota(formData)
        .then(() => {
          this.$toast?.success('Nota actualizada exitosamente')
          this.refrescaTbl()
          this.showEdita = false
          this.showNotaModal = false
        })
        .catch((err) => {
          console.error('Error al actualizar nota:', err)
          this.$toast?.error(err.response?.data?.message || 'Error al actualizar la nota')
        })
    }
  },

  mounted() {
    this.isLoading = true

    Balance.getReportes()
      .then((result) => {
        this.reportes = Array.isArray(result.data.reportes)
          ? result.data.reportes
          : (result.data.reportes[0] || [])
      })
      .catch((error) => {
        console.error('Error al cargar reportes:', error)
      })

    PlanCuentas.getVigenciaPlancuentas()
      .then((res) => {
        this.listVigencia = res.data.getVigencia || []
      })
      .catch((err) => {
        console.error('Error al cargar vigencias:', err)
      })
      .finally(() => {
        this.isLoading = false
      })

    Company.getAllCompanies()
      .then((result) => {
        this.listCompany = result.data.companies || []
        this.showSelCompany = true
      })
      .catch((err) => {
        console.error('Error al cargar compañías:', err)
      })
  }
}
</script>

<style src="../../main.css"></style>

<style scoped>
/* ============ FILTROS PRINCIPALES ============ */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

/* ============ HEADER ============ */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ============ TOOLBAR DE TABLAS ============ */
.notas-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.detail-toolbar-left {
    display: flex;
    align-items: center;
    flex: 1;
}

.detail-toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.05);
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Botón Volver del detalle */
.detail-toolbar-left .btn-secondary {
    background-color: #6c757d !important;
    border-color: #6c757d !important;
    color: white !important;
}

.detail-toolbar-left .btn-secondary:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}

.detail-toolbar-left .btn-secondary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Animación del detalle */
.detail-content {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ============ MODAL DE PÁRRAFO ============ */

.parrafo-dialog :deep(.modal-dialog) {
    max-width: 90vw;
    width: 90vw;
    margin: 1.75rem auto;
}

.parrafo-dialog :deep(.modal-content) {
    max-height: 90vh;
    border-radius: 8px;
    overflow: hidden;
}

.parrafo-dialog :deep(.modal-body) {
    padding: 1.25rem 1.5rem;
}

/* Forzar el uniform-modal-body a ancho completo dentro de este modal */
.parrafo-dialog :deep(.uniform-modal-body) {
    max-width: none !important;
    width: 100% !important;
}

/* Labels e inputs */
.parrafo-dialog :deep(.form-label) {
    font-size: 15px;
    color: #2c3e50;
    margin-bottom: 0;
    font-style: normal;
    font-weight: 600;
}

.parrafo-dialog :deep(.form-input),
.parrafo-dialog :deep(.form-control),
.parrafo-dialog :deep(.custom-select) {
    font-size: 14px;
}

@media (max-width: 992px) {
    .parrafo-dialog :deep(.modal-dialog) {
        max-width: 95vw;
        width: 95vw;
        margin: 0.75rem auto;
    }
}

@media (max-width: 576px) {
    .parrafo-dialog :deep(.modal-dialog) {
        max-width: 98vw;
        width: 98vw;
        margin: 0.5rem auto;
    }

    .parrafo-dialog :deep(.modal-body) {
        padding: 1rem;
    }
}

/* ============ RESPONSIVE ============ */

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }

    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .detail-toolbar-left {
        flex-wrap: wrap;
        gap: 8px;
    }

    .parrafo-dialog :deep(.modal-dialog) {
        max-width: 95vw;
        width: 95vw;
        margin: 0.75rem auto;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }

    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }

    .niif-header .filters-container {
        gap: 4px;
    }

    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }

    .detail-toolbar-left {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .detail-toolbar-right {
        flex-wrap: wrap;
        gap: 4px;
    }

    .parrafo-dialog :deep(.modal-dialog) {
        max-width: 98vw;
        width: 98vw;
        margin: 0.5rem auto;
    }

    .parrafo-dialog :deep(.modal-body),
    .parrafo-dialog :deep(.reset-modal-body) {
        padding: 1rem;
    }
}
</style>